import React from 'react';
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import type { AgentConfigurationCardProps } from '../../../../types/features/storeDetails/agentConfigurationCard';
import {
  adminCheckboxSx,
  adminCombineSx,
  adminDividerSx,
  adminFieldLabelSx,
  adminFormFieldSx,
  adminInlineControlLabelSx,
} from '../../../../styles/adminComponentStyles';

type AgentConfigurationScannerProxyProps = Pick<AgentConfigurationCardProps, 'control'>;

const AgentConfigurationScannerProxy: React.FC<AgentConfigurationScannerProxyProps> = ({ control }) => {
  const autoDetect = useWatch({ control, name: 'ScannerProxy.AutoDetect' });
  const disableAutocomplete = {
    autoComplete: 'off',
  };

  return (
    <Box sx={adminCombineSx(adminDividerSx, { borderTopStyle: 'solid', borderTopWidth: 1, mb: 3, pt: 2 })}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={adminFieldLabelSx}>
          Scanner Proxy
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Controller
            name="ScannerProxy.AutoDetect"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value !== false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={adminCheckboxSx}
                  />
                }
                label="Auto Detect"
                sx={adminInlineControlLabelSx}
              />
            )}
          />
          <Controller
            name="ScannerProxy.Enabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={adminCheckboxSx}
                  />
                }
                label="Is Enabled"
                sx={adminInlineControlLabelSx}
              />
            )}
          />
        </Box>
      </Box>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: autoDetect === false ? 6 : 12 }}>
          <Controller
            name="ScannerProxy.Port"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="Enter scanner proxy port"
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                autoComplete="off"
                slotProps={{ htmlInput: { ...disableAutocomplete, name: 'lr-scanner-proxy-port' } }}
                sx={adminFormFieldSx}
              />
            )}
          />
        </Grid>
        {autoDetect === false ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="ScannerProxy.BaudRate"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  size="small"
                  fullWidth
                  label="Baud Rate"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  autoComplete="off"
                  slotProps={{ htmlInput: { ...disableAutocomplete, name: 'lr-scanner-proxy-baud-rate' } }}
                  sx={adminFormFieldSx}
                />
              )}
            />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default React.memo(AgentConfigurationScannerProxy);
